import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createMarketplaceApi } from "../api";
import {
  formatLabel,
  opportunityStatusTone,
  priorityTone,
  severityTone,
  signalStatusTone,
} from "../labels";
import { useMarketplaceQueue } from "../useMarketplaceQueue";
import type { OpportunityDto, SignalDto } from "../types";
import {
  DetectSignalDialog,
  IdentifyOpportunityDialog,
} from "../components/MarketplaceDialogs";

const HUB_QUEUES = [
  {
    to: "/app/marketplace/signals",
    title: "Health signals",
    body: "Detected → validate → monitor / require action → resolve. Insight never mutates source records.",
  },
  {
    to: "/app/marketplace/opportunities",
    title: "Opportunities",
    body: "Identify → prioritize → assign ownership → realize / decline / close. Independent of Signals.",
  },
] as const;

const SIGNAL_STATUSES = [
  "detected",
  "validated",
  "monitoring",
  "action_required",
  "resolved",
  "archived",
] as const;

const OPPORTUNITY_STATUSES = [
  "identified",
  "validated",
  "prioritized",
  "assigned",
  "in_progress",
  "realized",
  "declined",
  "closed",
] as const;

const SEVERITIES = ["low", "normal", "high", "urgent", "critical"] as const;
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"] as const;

const SIGNAL_TYPES = [
  "supply_gap",
  "demand_gap",
  "utilization",
  "reliability",
  "conversion",
  "growth_bottleneck",
  "geographic_expansion",
  "future_governed_type",
] as const;

const TARGET_TYPES = [
  "market",
  "sport",
  "marketplace_category",
  "business_type",
  "business",
  "bookable_resource",
  "community",
  "creator_program",
] as const;

interface HubSlice {
  actionRequired: SignalDto[];
  highSeverity: SignalDto[];
  openOpportunities: OpportunityDto[];
  recentSignals: SignalDto[];
  recentOpportunities: OpportunityDto[];
  actionRequiredHasMore: boolean;
  highSeverityHasMore: boolean;
  openOpportunitiesHasMore: boolean;
}

/** The "Signals and opportunities" tab of the Marketplace Health page. */
export function MarketplaceSignalsPanel() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketplaceApi(client), [client]);

  const [slice, setSlice] = useState<HubSlice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        actionRequired,
        highSeverity,
        openOpportunities,
        recentSignals,
        recentOpportunities,
      ] = await Promise.all([
        api.listSignals({
          status: "action_required",
          limit: 10,
          sort: "-detected_at",
        }),
        api.listSignals({
          severity: "urgent,critical",
          limit: 10,
          sort: "-detected_at",
        }),
        api.listOpportunities({
          status: "prioritized,assigned,in_progress",
          limit: 10,
          sort: "-identified_at",
        }),
        api.listSignals({ limit: 8, sort: "-detected_at" }),
        api.listOpportunities({ limit: 8, sort: "-identified_at" }),
      ]);
      setSlice({
        actionRequired: actionRequired.signals,
        highSeverity: highSeverity.signals,
        openOpportunities: openOpportunities.opportunities,
        recentSignals: recentSignals.signals,
        recentOpportunities: recentOpportunities.opportunities,
        actionRequiredHasMore: Boolean(
          actionRequired.pagination.next_cursor,
        ),
        highSeverityHasMore: Boolean(highSeverity.pagination.next_cursor),
        openOpportunitiesHasMore: Boolean(
          openOpportunities.pagination.next_cursor,
        ),
      });
    } catch (err) {
      setError(apiErrorMessage(err));
      setSlice(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Signals and opportunities"
        description="Problems and growth ideas staff have logged about the marketplace, with who owns them and what happened next. Log one from the Health tab, or here. Closing a signal here never changes the business, court or booking it is about."
        actions={
          <Button variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      {error ? (
        <Alert tone="danger" title="Unable to load marketplace health">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading marketplace health…" /> : null}

      {!loading && slice ? (
        <Flex gap={16} wrap>
          <Card
            header={
              <Text role="title" as="h3">
                Action required
              </Text>
            }
          >
            <Stack gap={8}>
              <Text role="h2" as="p">
                {slice.actionRequired.length}
                {slice.actionRequiredHasMore ? "+" : ""}
              </Text>
              <Text role="small" color="secondary" as="p">
                Signals with status action_required (this page)
              </Text>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    "/app/marketplace/signals?status=action_required",
                  )
                }
              >
                Open queue
              </Button>
            </Stack>
          </Card>
          <Card
            header={
              <Text role="title" as="h3">
                Urgent / critical
              </Text>
            }
          >
            <Stack gap={8}>
              <Text role="h2" as="p">
                {slice.highSeverity.length}
                {slice.highSeverityHasMore ? "+" : ""}
              </Text>
              <Text role="small" color="secondary" as="p">
                Signals with severity urgent or critical (this page)
              </Text>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    "/app/marketplace/signals?severity=urgent%2Ccritical",
                  )
                }
              >
                Open queue
              </Button>
            </Stack>
          </Card>
          <Card
            header={
              <Text role="title" as="h3">
                Open opportunities
              </Text>
            }
          >
            <Stack gap={8}>
              <Text role="h2" as="p">
                {slice.openOpportunities.length}
                {slice.openOpportunitiesHasMore ? "+" : ""}
              </Text>
              <Text role="small" color="secondary" as="p">
                prioritized, assigned, or in_progress (this page)
              </Text>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    "/app/marketplace/opportunities?status=prioritized%2Cassigned%2Cin_progress",
                  )
                }
              >
                Open queue
              </Button>
            </Stack>
          </Card>
        </Flex>
      ) : null}

      <Flex gap={16} wrap>
        {HUB_QUEUES.map((queue) => (
          <Card
            key={queue.to}
            header={
              <Text role="title" as="h3">
                {queue.title}
              </Text>
            }
          >
            <Stack gap={12}>
              <Text role="small" color="secondary" as="p">
                {queue.body}
              </Text>
              <Button
                variant="secondary"
                onClick={() => void navigate(queue.to)}
              >
                Open queue
              </Button>
            </Stack>
          </Card>
        ))}
      </Flex>

      {!loading && slice ? (
        <Flex gap={16} wrap>
          <Card
            header={
              <Text role="title" as="h3">
                Recent signals
              </Text>
            }
          >
            {slice.recentSignals.length === 0 ? (
              <EmptyState
                title="No signals"
                description="Detect a signal when evidence warrants review."
                reason="none"
              />
            ) : (
              <Stack gap={8}>
                {slice.recentSignals.map((row) => (
                  <Button
                    key={row.id}
                    variant="ghost"
                    onClick={() =>
                      void navigate(`/app/marketplace/signals/${row.id}`)
                    }
                  >
                    <Flex gap={8} wrap>
                      <StatusChip tone={signalStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                      <StatusChip tone={severityTone(row.severity)}>
                        {formatLabel(row.severity)}
                      </StatusChip>
                      <Text role="small" as="span">
                        {formatLabel(row.signal_type)} ·{" "}
                        {formatLabel(row.target_type)}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </Stack>
            )}
          </Card>
          <Card
            header={
              <Text role="title" as="h3">
                Recent opportunities
              </Text>
            }
          >
            {slice.recentOpportunities.length === 0 ? (
              <EmptyState
                title="No opportunities"
                description="Identify an opportunity when prioritization is needed."
                reason="none"
              />
            ) : (
              <Stack gap={8}>
                {slice.recentOpportunities.map((row) => (
                  <Button
                    key={row.id}
                    variant="ghost"
                    onClick={() =>
                      void navigate(
                        `/app/marketplace/opportunities/${row.id}`,
                      )
                    }
                  >
                    <Flex gap={8} wrap>
                      <StatusChip tone={opportunityStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                      <StatusChip tone={priorityTone(row.priority)}>
                        {formatLabel(row.priority)}
                      </StatusChip>
                      <Text role="small" as="span">
                        {formatLabel(row.target_type)} / {row.target_id}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </Stack>
            )}
          </Card>
        </Flex>
      ) : null}
    </Stack>
  );
}

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  actions,
  error,
  loading,
  empty,
  children,
  page,
  nextCursor,
  onNext,
  onPrevious,
  onRefresh,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  actions?: ReactNode;
  error: string | null;
  loading: boolean;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
}) {
  usePageBreadcrumbs(breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8} wrap>
            {actions}
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>{filters}</Filters>
      {error ? (
        <Alert tone="danger" title="Load failed">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && empty ? (
        <EmptyState
          title={`No ${title.toLowerCase()}`}
          description="Adjust filters or create a record."
          reason="none"
        />
      ) : null}
      {!loading && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function SignalsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketplaceApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listSignals({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.severity
            ? { severity: params.extra.severity }
            : {}),
          ...(params.extra.signal_type
            ? { signal_type: params.extra.signal_type }
            : {}),
          ...(params.extra.market_id
            ? { market_id: params.extra.market_id }
            : {}),
          ...(params.extra.target_type
            ? { target_type: params.extra.target_type }
            : {}),
          ...(params.extra.target_id
            ? { target_id: params.extra.target_id }
            : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return { items: list.signals, pagination: list.pagination };
      },
    [api],
  );

  const queue = useMarketplaceQueue<SignalDto>(loadPage, {
    sort: "-detected_at",
  });

  return (
    <>
      <QueueShell
        title="Health signals"
        description="Marketplace Health Signals. Validate evidence, set ownership, require action, or resolve. Does not change Business, Booking, Finance, Support, or Trust & Safety records."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Marketplace Health", to: "/app/marketplace" },
          { label: "Signals" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>Detect signal</Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...SIGNAL_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Severity"
              value={queue.extra.severity ?? ""}
              onChange={(event) =>
                queue.setExtra("severity", event.target.value)
              }
              options={[
                { value: "", label: "All severities" },
                { value: "urgent,critical", label: "Urgent + critical" },
                ...SEVERITIES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Signal type"
              value={queue.extra.signal_type ?? ""}
              onChange={(event) =>
                queue.setExtra("signal_type", event.target.value)
              }
              options={[
                { value: "", label: "All types" },
                ...SIGNAL_TYPES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Target type"
              value={queue.extra.target_type ?? ""}
              onChange={(event) =>
                queue.setExtra("target_type", event.target.value)
              }
              options={[
                { value: "", label: "All targets" },
                ...TARGET_TYPES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Target ID"
              value={queue.extra.target_id ?? ""}
              onChange={(event) =>
                queue.setExtra("target_id", event.target.value)
              }
            />
            <Input
              label="Market ID"
              value={queue.extra.market_id ?? ""}
              onChange={(event) =>
                queue.setExtra("market_id", event.target.value)
              }
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "-detected_at", label: "Detected (newest)" },
                { value: "detected_at", label: "Detected (oldest)" },
                { value: "severity", label: "Severity" },
                { value: "status", label: "Status" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Detected</Th>
              <Th>Type</Th>
              <Th>Severity</Th>
              <Th>Status</Th>
              <Th>Target</Th>
              <Th>Owner</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/marketplace/signals/${row.id}`)
                }
              >
                <Td>{row.detected_at}</Td>
                <Td>{formatLabel(row.signal_type)}</Td>
                <Td>
                  <StatusChip tone={severityTone(row.severity)}>
                    {formatLabel(row.severity)}
                  </StatusChip>
                </Td>
                <Td>
                  <StatusChip tone={signalStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>
                  {formatLabel(row.target_type)} / {row.target_id}
                </Td>
                <Td>{row.owner_user_id ?? "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <DetectSignalDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createSignal(body);
            toast.push({ title: "Signal detected", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/marketplace/signals/${result.signal.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}

export function OpportunitiesQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketplaceApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listOpportunities({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.priority
            ? { priority: params.extra.priority }
            : {}),
          ...(params.extra.market_id
            ? { market_id: params.extra.market_id }
            : {}),
          ...(params.extra.target_type
            ? { target_type: params.extra.target_type }
            : {}),
          ...(params.extra.target_id
            ? { target_id: params.extra.target_id }
            : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return { items: list.opportunities, pagination: list.pagination };
      },
    [api],
  );

  const queue = useMarketplaceQueue<OpportunityDto>(loadPage, {
    sort: "-identified_at",
  });

  return (
    <>
      <QueueShell
        title="Opportunities"
        description="Marketplace Opportunities. Prioritize and assign ownership with rationale. Execution stays in owning departments — no automatic remediation here."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Marketplace Health", to: "/app/marketplace" },
          { label: "Opportunities" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            Identify opportunity
          </Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                {
                  value: "prioritized,assigned,in_progress",
                  label: "Open (prioritized+)",
                },
                ...OPPORTUNITY_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Priority"
              value={queue.extra.priority ?? ""}
              onChange={(event) =>
                queue.setExtra("priority", event.target.value)
              }
              options={[
                { value: "", label: "All priorities" },
                { value: "urgent,critical", label: "Urgent + critical" },
                ...PRIORITIES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Target type"
              value={queue.extra.target_type ?? ""}
              onChange={(event) =>
                queue.setExtra("target_type", event.target.value)
              }
              options={[
                { value: "", label: "All targets" },
                ...TARGET_TYPES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Target ID"
              value={queue.extra.target_id ?? ""}
              onChange={(event) =>
                queue.setExtra("target_id", event.target.value)
              }
            />
            <Input
              label="Market ID"
              value={queue.extra.market_id ?? ""}
              onChange={(event) =>
                queue.setExtra("market_id", event.target.value)
              }
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "-identified_at", label: "Identified (newest)" },
                { value: "identified_at", label: "Identified (oldest)" },
                { value: "priority", label: "Priority" },
                { value: "status", label: "Status" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Identified</Th>
              <Th>Priority</Th>
              <Th>Status</Th>
              <Th>Target</Th>
              <Th>Owner</Th>
              <Th>Closed</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/marketplace/opportunities/${row.id}`)
                }
              >
                <Td>{row.identified_at}</Td>
                <Td>
                  <StatusChip tone={priorityTone(row.priority)}>
                    {formatLabel(row.priority)}
                  </StatusChip>
                </Td>
                <Td>
                  <StatusChip tone={opportunityStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>
                  {formatLabel(row.target_type)} / {row.target_id}
                </Td>
                <Td>{row.owner_user_id ?? "—"}</Td>
                <Td>{row.closed_at ?? "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <IdentifyOpportunityDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createOpportunity(body);
            toast.push({ title: "Opportunity identified", tone: "success" });
            setCreateOpen(false);
            void navigate(
              `/app/marketplace/opportunities/${result.opportunity.id}`,
            );
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}
